import { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CheckIcon from '@mui/icons-material/Check';
import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { dataGridColumns } from '../../utils/helper';

import { DataGridIdentifiers, FormErrorUI, InputUI, AddButtonUi, TimePickerUi, DatagridPremiumUi } from '../Interface';
import dayjs from 'dayjs';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';

function EditToolbar(props) {
  const { disabled, push, defaultField } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultField.id = uuidv4();
    await push(defaultField);

    const fieldsToValidate = [
      `companyBreakLine.${values.companyBreakLine.length ?? 0}.name`,
      `companyBreakLine.${values.companyBreakLine.length ?? 0}.fromTime`,
      `companyBreakLine.${values.companyBreakLine.length ?? 0}.toTime`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const CompanyBreakLine = ({ disabled, defaultField }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'name', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getValueIndex = (values, id) => {
    return values.companyBreakLine.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridColumns([
      {
        field: 'name',
        type: 'string',
        headerName: 'Szünet neve',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`companyBreakLine.${valueIndex}.name`}
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.companyBreakLine?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`companyBreakLine.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`companyBreakLine.${valueIndex}.name`)}
            />
          );
        },
      },

      {
        field: 'fromTime',
        headerName: 'Szünet kezdete',
        type: 'time',
        editable: true,
        valueGetter: (_data, row) => {
          return row.fromTime ? new Date(row.fromTime) : null;
        },
        renderCell: (data) => {
          if (!data.row.fromTime) return null;

          const utcTime = dayjs.utc(data.row.fromTime);
          const hours = utcTime.hour();
          const minutes = utcTime.minute();
          const todayWithTimeUtc = dayjs.utc().set('hour', hours).set('minute', minutes);
          const localTime = todayWithTimeUtc.local();

          return localTime.format('HH:mm');
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const timeValue = values.companyBreakLine?.[valueIndex]?.fromTime;

          let localTime = null;
          if (timeValue) {
            const utcTime = dayjs.utc(timeValue);
            const todayWithTimeUtc = dayjs.utc().set('hour', utcTime.hour()).set('minute', utcTime.minute());
            localTime = todayWithTimeUtc.local();
          }

          return (
            <Field
              name={`companyBreakLine.${valueIndex}.fromTime`}
              component={TimePickerUi}
              helperText={<FormErrorUI message={errors?.companyBreakLine?.[valueIndex]?.fromTime} />}
              variant="standard"
              value={localTime}
              onChange={(newDate) => {
                const today = dayjs();
                const timeStr = dayjs(newDate).format('HH:mm');
                const dateTime = dayjs(`${today.format('YYYY-MM-DD')}T${timeStr}`)
                  .second(0)
                  .millisecond(0)
                  .toDate();

                setFieldValue(`companyBreakLine.${valueIndex}.fromTime`, dateTime);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: dateTime,
                });
              }}
              onBlur={async () => await validateField(`companyBreakLine.${valueIndex}.fromTime`)}
            />
          );
        },
      },
      {
        field: 'toTime',
        headerName: 'Szünet vége',
        editable: true,
        type: 'time',
        valueGetter: (_data, row) => {
          return row.toTime ? new Date(row.toTime) : null;
        },
        renderCell: (data) => {
          if (!data.row.fromTime) return null;

          const utcTime = dayjs.utc(data.row.toTime);
          const hours = utcTime.hour();
          const minutes = utcTime.minute();
          const todayWithTimeUtc = dayjs.utc().set('hour', hours).set('minute', minutes);
          const localTime = todayWithTimeUtc.local();

          return localTime.format('HH:mm');
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const timeValue = values.companyBreakLine?.[valueIndex]?.toTime;

          let localTime = null;
          if (timeValue) {
            const utcTime = dayjs.utc(timeValue);
            const todayWithTimeUtc = dayjs.utc().set('hour', utcTime.hour()).set('minute', utcTime.minute());
            localTime = todayWithTimeUtc.local();
          }

          return (
            <Field
              name={`companyBreakLine.${valueIndex}.toTime`}
              component={TimePickerUi}
              helperText={<FormErrorUI message={errors?.companyBreakLine?.[valueIndex]?.toTime} />}
              variant="standard"
              value={localTime}
              onChange={(newDate) => {
                const fromRaw = values.companyBreakLine?.[valueIndex]?.fromTime;

                let localTime = null;
                if (fromRaw) {
                  const utcTime = dayjs.utc(fromRaw);
                  const todayWithTimeUtc = dayjs.utc().set('hour', utcTime.hour()).set('minute', utcTime.minute());
                  localTime = todayWithTimeUtc.local();
                }

                if (localTime) {
                  const fromLocal = localTime.local();
                  const selectedLocal = dayjs(newDate);
                  const today = dayjs();

                  const fromTimeToday = dayjs(`${today.format('YYYY-MM-DD')}T${fromLocal.format('HH:mm')}`);
                  const selectedTimeToday = dayjs(`${today.format('YYYY-MM-DD')}T${selectedLocal.format('HH:mm')}`);

                  if (selectedTimeToday.isBefore(fromTimeToday)) {
                    addNotification({
                      content: 'A szünet vége nem lehet korábbi, mint a kezdete.',
                      type: NotificationType.ERROR,
                    });
                    return;
                  }
                }

                const today = dayjs();
                const timeStr = dayjs(newDate).format('HH:mm');
                const dateTime = dayjs(`${today.format('YYYY-MM-DD')}T${timeStr}`)
                  .second(0)
                  .millisecond(0)
                  .toDate();

                setFieldValue(`companyBreakLine.${valueIndex}.toTime`, dateTime);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: dateTime,
                });
              }}
              onBlur={async () => await validateField(`companyBreakLine.${valueIndex}.toTime`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto mb-5">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="companyBreakLine">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridInitialState={initialState}
                datagridIdentifier={DataGridIdentifiers.companyBreakLine}
                height={620}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.companyBreakLine ?? []}
                headerFilters={false}
                slots={{
                  toolbar: EditToolbar,
                }}
                getRowHeight={() => 'auto'}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultField,
                  },
                }}
                editMode="row"
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default CompanyBreakLine;
